/**
 * PROFILE SELECT — the first screen at every opening: Isaac / Bao / Papa.
 *
 * Kids tap their avatar; the device's fingerprint sensor confirms it's really them
 * (native Android BiometricPrompt, see kid/biometric.ts) before entering — with a
 * 4-digit PIN fallback (0000, shared — not a privacy wall between siblings, just a
 * fallback if the sensor isn't enrolled/available, per the brainstorm synthesis).
 * Papa is gated by his own PIN (1984). NOTE: none of this is a hard security
 * boundary — the real boundary is the device-token-gated backend; these gates only
 * keep a curious child from landing in the wrong space. Designed for a smartphone,
 * portrait, one-handed.
 */
import { useState } from "react";
import { Profile, ChildId, CHILD_NAMES } from "./childSession";
import { tryFingerprint } from "./biometric";

// AUTHORING / brainstorm synthesis: private family-use PINs, not real secrets.
const PARENT_PIN = "1984";
const CHILD_FALLBACK_PIN = "0000"; // used only if fingerprint fails/unavailable

const TILES: { id: Profile; name: string; emoji: string; tint: string }[] = [
  { id: "isaac", name: "Isaac", emoji: "🦖", tint: "#39d0c8" },
  { id: "bao", name: "Bao", emoji: "🦄", tint: "#c58cff" },
  { id: "papa", name: "Papa", emoji: "🛡️", tint: "#f0b03a" },
];

export function ProfileSelect({ onSelect }: { onSelect: (p: Profile) => void }) {
  const [pinTarget, setPinTarget] = useState<{ pin: string; title: string; onOk: () => void } | null>(null);
  const [checking, setChecking] = useState<ChildId | null>(null);

  const tapChild = async (id: ChildId) => {
    if (checking) return;
    setChecking(id);
    const ok = await tryFingerprint(`Bonjour ${CHILD_NAMES[id]}, pose ton doigt`);
    setChecking(null);
    if (ok) {
      onSelect(id);
      return;
    }
    // No sensor / not enrolled / cancelled — fall back to the shared PIN.
    setPinTarget({ pin: CHILD_FALLBACK_PIN, title: CHILD_NAMES[id], onOk: () => onSelect(id) });
  };

  return (
    <div style={S.screen}>
      <div style={S.title}>QUI EST À L'APPAREIL&nbsp;?</div>
      <div style={S.row}>
        {TILES.map((t) => (
          <button
            key={t.id}
            style={{ ...S.tile, borderColor: t.tint, boxShadow: `0 0 24px ${t.tint}33`, opacity: checking && checking !== t.id ? 0.4 : 1 }}
            disabled={!!checking}
            onClick={() =>
              t.id === "papa"
                ? setPinTarget({ pin: PARENT_PIN, title: "Espace parent", onOk: () => onSelect("papa") })
                : tapChild(t.id as ChildId)
            }
          >
            <span style={{ ...S.avatar, textShadow: `0 0 18px ${t.tint}` }}>{t.emoji}</span>
            <span style={{ ...S.name, color: t.tint }}>{t.name}</span>
            {checking === t.id && <span style={S.checking}>vérification…</span>}
          </button>
        ))}
      </div>
      {pinTarget && (
        <PinPad
          title={pinTarget.title}
          targetPin={pinTarget.pin}
          onCancel={() => setPinTarget(null)}
          onSuccess={pinTarget.onOk}
        />
      )}
    </div>
  );
}

function PinPad({
  title,
  targetPin,
  onSuccess,
  onCancel,
}: {
  title: string;
  targetPin: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState(false);

  const push = (d: string) => {
    if (pin.length >= 4) return;
    const next = pin + d;
    setErr(false);
    setPin(next);
    if (next.length === 4) {
      if (next === targetPin) {
        setTimeout(onSuccess, 120);
      } else {
        setErr(true);
        setTimeout(() => setPin(""), 500);
      }
    }
  };

  return (
    <div style={S.overlay} onClick={onCancel}>
      <div style={S.pinCard} onClick={(e) => e.stopPropagation()}>
        <div style={S.pinTitle}>{title}</div>
        <div style={S.dots}>
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              style={{
                ...S.dot,
                background: i < pin.length ? (err ? "#ff5d5d" : "#f0b03a") : "transparent",
                borderColor: err ? "#ff5d5d" : "#f0b03a",
              }}
            />
          ))}
        </div>
        <div style={S.pad}>
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
            <button key={d} style={S.key} onClick={() => push(d)}>
              {d}
            </button>
          ))}
          <button style={{ ...S.key, ...S.keyGhost }} onClick={onCancel}>
            ✕
          </button>
          <button style={S.key} onClick={() => push("0")}>
            0
          </button>
          <button
            style={{ ...S.key, ...S.keyGhost }}
            onClick={() => {
              setErr(false);
              setPin((p) => p.slice(0, -1));
            }}
          >
            ⌫
          </button>
        </div>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  screen: {
    position: "fixed",
    inset: 0,
    background: "radial-gradient(120% 120% at 50% 0%, #0a1622 0%, #05070a 60%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "system-ui, sans-serif",
    userSelect: "none",
  },
  title: {
    color: "#8fb4c8",
    letterSpacing: "0.28em",
    fontSize: "clamp(14px, 2.4vw, 22px)",
    marginBottom: "6vh",
    fontWeight: 600,
    textAlign: "center",
    padding: "0 16px",
  },
  row: { display: "flex", gap: "clamp(16px, 4vw, 40px)", flexWrap: "wrap", justifyContent: "center" },
  tile: {
    width: "clamp(120px, 38vw, 220px)",
    height: "clamp(140px, 42vw, 260px)",
    background: "rgba(10,22,34,0.7)",
    border: "2px solid",
    borderRadius: 24,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "clamp(8px, 2vw, 20px)",
    cursor: "pointer",
    transition: "transform 0.12s ease, opacity 0.2s ease",
    backdropFilter: "blur(2px)",
  },
  avatar: { fontSize: "clamp(40px, 12vw, 90px)", lineHeight: 1 },
  name: { fontSize: "clamp(16px, 4vw, 30px)", fontWeight: 700, letterSpacing: "0.06em" },
  checking: { fontSize: 11, color: "#8fb4c8", letterSpacing: "0.08em" },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,4,7,0.82)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
  },
  pinCard: {
    background: "#0a1622",
    border: "1px solid #f0b03a55",
    borderRadius: 20,
    padding: "28px 32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 22,
  },
  pinTitle: { color: "#f0b03a", letterSpacing: "0.18em", fontSize: 16, fontWeight: 600 },
  dots: { display: "flex", gap: 16 },
  dot: { width: 16, height: 16, borderRadius: "50%", border: "2px solid", transition: "background 0.1s" },
  pad: { display: "grid", gridTemplateColumns: "repeat(3, 72px)", gap: 14 },
  key: {
    height: 72,
    borderRadius: 16,
    border: "1px solid #2a4256",
    background: "rgba(20,36,52,0.9)",
    color: "#dbe9f2",
    fontSize: 26,
    cursor: "pointer",
  },
  keyGhost: { background: "transparent", color: "#7f9bb0", borderColor: "#1c3040" },
};
